import { Permission } from '../../api/roles.dao';
import {
    IApiClient,
    IApiResponse,
    IBaseRequestOptions,
    IFileRequestOptions,
    IFileResponse,
    IRequestOptions,
    IRequestUrl,
    IResponseBase,
    RequestContentType,
    ResponseParser,
} from '../api-client';
import { IRuntimeConfiguration } from '../runtime-configuration';
import { hasAnyPermission } from '../utils/has-permissions';
import { transformObjectToFormDataCompatible } from '../utils/common.utils';
import { FILE_UPLOAD_UNSUPPORTED_TYPE_ERROR_MESSAGE } from '../../constants/file.constants';

const defaultResponseParser = <T>(response: Response, responseBody: string) => {
    return responseBody && responseBody.length > 0 ? JSON.parse(responseBody) : null as T;
};

const absoluteUrlRegex = /^https?:\/\//i;

class ApiClient implements IApiClient {
    constructor(private configuration: IRuntimeConfiguration) {

    }

    public delete<TResponse, TRequest>(requestUrl: IRequestUrl, parameters: TRequest, options?: IRequestOptions<TResponse>):
        Promise<IApiResponse<TResponse>> {
        return this.sendRequest(
            requestUrl,
            parameters,
            'DELETE',
            this.buildTextSuccessProcessor(options && options.responseParser),
            options,
        );
    }

    public get<TResponse, TRequest>(requestUrl: IRequestUrl, parameters: TRequest, options?: IRequestOptions<TResponse>):
        Promise<IApiResponse<TResponse>> {
        return this.sendRequest(
            requestUrl,
            parameters,
            'GET',
            this.buildTextSuccessProcessor(options && options.responseParser),
            options,
        );
    }

    public post<TResponse, TRequest>(
        requestUrl: IRequestUrl,
        payload: TRequest,
        options?: IRequestOptions<TResponse>):
        Promise<IApiResponse<TResponse>> {
        return this.sendRequest(
            requestUrl,
            payload,
            'POST',
            this.buildTextSuccessProcessor(options && options.responseParser),
            options,
        );
    }

    public put<TResponse, TRequest>(
        requestUrl: IRequestUrl,
        payload: TRequest,
        options?: IRequestOptions<TResponse>):
        Promise<IApiResponse<TResponse>> {
        return this.sendRequest(
            requestUrl,
            payload,
            'PUT',
            this.buildTextSuccessProcessor(options && options.responseParser),
            options,
        );
    }

    public patch<TResponse, TRequest>(
        requestUrl: IRequestUrl,
        payload: TRequest,
        options?: IRequestOptions<TResponse>):
        Promise<IApiResponse<TResponse>> {
        return this.sendRequest(
            requestUrl,
            payload,
            'PATCH',
            this.buildTextSuccessProcessor(options && options.responseParser),
            options,
        );
    }

    public async getFile<TRequest>(requestUrl: IRequestUrl, parameters: TRequest, options?: IFileRequestOptions):
        Promise<IFileResponse> {
        const method = options?.requestInit?.method ?? 'GET';
        const fileSendResult = await this.sendRequest(requestUrl, parameters, method, this.blobSuccessProcessor, options);
        // in case no 'download' option is provided - consider file should be downloaded
        if (!(options && options.download === false)) {
            let fileName = options && options.fileName;
            if (fileName == null) {
                const contentDisposition = fileSendResult.response.headers.get('Content-Disposition');
                fileName = this.extractFileNameFromContentDispositionHeader(contentDisposition);
            }

            const dataUrl = window.URL.createObjectURL(fileSendResult.rawResponse);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = dataUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(dataUrl);
        }

        return fileSendResult;
    }

    private extractFileNameFromContentDispositionHeader(headerValue: string) {
        const regexp = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;

        const dispositionMatch = regexp.exec(headerValue);
        let fileName = dispositionMatch[1];
        const quoteIfAny = dispositionMatch[2];
        if (quoteIfAny) {
            fileName = fileName.replace(new RegExp(quoteIfAny, 'g'), '');
        }

        return fileName;
    }

    private async blobSuccessProcessor(response: Response): Promise<IFileResponse> {
        const responseBlob = await response.blob();
        return {
            response: response,
            rawResponse: responseBlob,
        };
    }

    private buildTextSuccessProcessor<TResponse>(customParser?: ResponseParser<TResponse>) {
        return async (response: Response) => {
            const responseText = await response.text();

            const responseParser: ResponseParser<TResponse> = customParser || defaultResponseParser;
            const parsedResponse = responseParser(response, responseText);
            return {
                response: response,
                parsedResponse: parsedResponse,
                rawResponse: responseText,
            };
        };
    }

    private isPermissionDenied(requestUrl: IRequestUrl, requiredPermissions: Permission[]): boolean {
        if (typeof requestUrl !== 'string' && 'permissions' in requestUrl) {
            return !hasAnyPermission(requiredPermissions);
        }
        return requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions);
    }

    private sendRequest<T extends IResponseBase, TRequest>(
        requestUrl: IRequestUrl,
        payload: TRequest,
        method: string,
        successProcessor: (response: Response) => Promise<T>,
        options?: IBaseRequestOptions,
    ): Promise<T> {
        let requestInit: RequestInit = {
            method: method,
            headers: {
                Accept: 'application/json',
            },
        };

        let url: string;
        let requiredPermissions: Permission[] = [];

        if (typeof requestUrl === 'string') {
            url = requestUrl;
        } else {
            url = requestUrl.url;
            requiredPermissions = 'permissions' in requestUrl
                ? requestUrl.permissions
                : [requestUrl.permission];
        }

        if (!(absoluteUrlRegex.exec(url))) {
            url = this.configuration.getApiRoot() + url;
        }

        const urlAndParams = this.replaceUrlParameters(url, payload);
        let binaryPayloadError: string = null;
        let contentType = RequestContentType.json;
        if (options != null && options.ContentType != null) {
            contentType = options.ContentType;
        }

        if (options != null && options.signal) {
            requestInit.signal = options.signal;
        }

        if (urlAndParams.refinedParams != null) {
            let uriParams = options && options.uriParams || {};

            if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
                const readableStreamContentType = [
                    RequestContentType.zip,
                    RequestContentType.xZip,
                    RequestContentType.imgGif,
                    RequestContentType.imgPng,
                    RequestContentType.imgJpeg,
                    RequestContentType.imgJpg,
                    RequestContentType.imgSvg,
                    RequestContentType.imgTiff,
                    RequestContentType.html,
                    RequestContentType.csv,
                    RequestContentType.xls,
                    RequestContentType.xlsx,
                    RequestContentType.doc,
                    RequestContentType.docx,
                    RequestContentType.ppt,
                    RequestContentType.pptx,
                    RequestContentType.txt,
                    RequestContentType.pdf,
                    RequestContentType.msg,
                    RequestContentType.ics,
                    RequestContentType.mp3,
                    RequestContentType.mpeg,
                    RequestContentType.wav,
                    RequestContentType.xWav,
                    RequestContentType.m4a,
                    RequestContentType.xM4a,
                    RequestContentType.audioWebm,
                    RequestContentType.ogg,
                    RequestContentType.flac,
                    RequestContentType.xFlac,
                    RequestContentType.aac,
                    RequestContentType.mp4,
                    RequestContentType.mov,
                    RequestContentType.quicktime,
                ];

                const readableStreamFontContentType = [
                    RequestContentType.woff2,
                    RequestContentType.eot,
                    RequestContentType.otf,
                    RequestContentType.ttf
                ];

                if (contentType === RequestContentType.formData) {
                    const objectToFormData = transformObjectToFormDataCompatible(urlAndParams.refinedParams);
                    const formData = new FormData();
                    for (const key of Object.keys(objectToFormData)) {
                        formData.append(key, objectToFormData[key]);
                    }
                    requestInit.body = formData;
                } else if (contentType === RequestContentType.woff ) {
                    requestInit.headers['Content-Type'] = 'font/woff';
                    requestInit.body = payload as unknown as ReadableStream<Uint8Array>;
                } else if (readableStreamFontContentType.includes(contentType)) {
                    requestInit.headers['Content-Type'] = contentType;
                    requestInit.body = payload as unknown as ReadableStream<Uint8Array>;
                } else if (readableStreamContentType.includes(contentType)) {
                    requestInit.body = payload as unknown as ReadableStream<Uint8Array>;
                } else if (contentType === RequestContentType.xZipCompressed) {
                    requestInit.body = payload as unknown as ReadableStream<Uint8Array>;
                    requestInit.headers['Content-Type'] = 'application/zip';
                } else if (payload instanceof File) {
                    binaryPayloadError = FILE_UPLOAD_UNSUPPORTED_TYPE_ERROR_MESSAGE(payload.name);
                } else {
                    requestInit.body = JSON.stringify(urlAndParams.refinedParams);
                    requestInit.headers['Content-Type'] = 'application/json; charset=UTF-8';
                }

            }
            if (method === 'GET' || method === 'DELETE') {
                uriParams = {
                    ...uriParams,
                    ...urlAndParams.refinedParams,
                };
            }
            urlAndParams.url += this.buildQuery(uriParams);
        }

        if (options && options.requestInit) {
            requestInit = {
                ...requestInit,
                ...options.requestInit,
                headers: {
                    ...requestInit.headers,
                    ...options.requestInit.headers || {},
                },
            };
        }

        return new Promise((resolve, reject) => {
            if (binaryPayloadError) {
                reject({
                    response: {
                        ok: false
                    },
                    rawResponse: JSON.stringify({
                        message: binaryPayloadError
                    }),
                });
            } else if (this.isPermissionDenied(requestUrl, requiredPermissions)) {
                reject({
                    response: {
                        ok: false
                    },
                    rawResponse: JSON.stringify({
                        message: 'Access denied'
                    }),
                });
            } else {
                fetch(urlAndParams.url, requestInit).then(response => {
                    if (response.ok) {
                        successProcessor(response).then(result => resolve(result));
                    } else {
                        response.text().then(responseBodyString => {
                            reject({
                                response: response,
                                parsedResponse: null,
                                rawResponse: responseBodyString,
                            });
                        });
                    }
                }, e => {
                    reject(e);
                });
            }
        });
    }

    private replaceUrlParameters<T>(url: string, requestParams: T) {
        if (requestParams == null) {
            return { url: url, refinedParams: null };
        }

        if (requestParams instanceof Array) {
            return { url: url, refinedParams: requestParams };
        }

        const paramsCopy = {...requestParams};
        let newUrl = url;
        if (requestParams instanceof Object) {
            for (const requestParam of Object.keys(requestParams)) {
                const paramToken = '{' + requestParam + '}';
                if (newUrl.indexOf(paramToken) !== -1) {
                    newUrl = newUrl.replace(paramToken, requestParams[requestParam]);
                    delete paramsCopy[requestParam];
                }
            }
        }

        return { url: newUrl, refinedParams: paramsCopy };
    }

    private buildQuery<T>(requestParams: T) {
        const urlParameters: string[] = [];
        if (requestParams instanceof Object) {
            for (const requestParam of Object.keys(requestParams)) {
                if (requestParams[requestParam] instanceof Array) {
                    for (const requestParamValue of requestParams[requestParam]) {
                        urlParameters.push(requestParam + '=' + encodeURIComponent(requestParamValue));
                    }
                } else {
                    urlParameters.push(requestParam + '=' + encodeURIComponent(requestParams[requestParam]));
                }
            }
        }
        if (urlParameters.length > 0) {
            return '?' + urlParameters.join('&');
        } else {
            return '';
        }
    }
}

export { ApiClient };
